// src/features/billing/BillingAccountFormDialog.tsx
// "+ New billing account" — create dialog for a B2B2C BillingAccount
// (CreateBillingAccount). This is a genuinely dense ~20-field request
// (name/email/phone, billing address, tax info, payment settings), so per
// docs/ux/14-forms.md it uses the 680px "dense form" dialog width with
// progressive disclosure: name/email/phone stay visible, address/tax/payment
// settings sit behind "+ more fields".

import { useState } from 'react';
import { useI18n } from '@react/hooks';
import { useBillingBillingAccount } from '@react/hooks';
import { FormDialog, TitleInput, FormRow } from '@/components/form';
import { Select } from '@/components/Select';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import type { BillingAccount } from '@core/types/billing';
import { errMessage } from './format';

export interface BillingAccountFormDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (account: BillingAccount) => void;
  onError: (message: string) => void;
}

const CURRENCY_OPTIONS = [
  { value: 'CURRENCY_USD', label: 'USD' },
  { value: 'CURRENCY_EUR', label: 'EUR' },
  { value: 'CURRENCY_RUB', label: 'RUB' },
  { value: 'CURRENCY_GBP', label: 'GBP' },
  { value: 'CURRENCY_JPY', label: 'JPY' },
  { value: 'CURRENCY_CNY', label: 'CNY' },
  { value: 'CURRENCY_KZT', label: 'KZT' },
  { value: 'CURRENCY_BYN', label: 'BYN' },
  { value: 'CURRENCY_AED', label: 'AED' },
  { value: 'CURRENCY_TRY', label: 'TRY' },
];

const BILLING_PERIOD_OPTIONS = [
  { value: 'BILLING_PERIOD_DAILY', label: 'Daily' },
  { value: 'BILLING_PERIOD_WEEKLY', label: 'Weekly' },
  { value: 'BILLING_PERIOD_BIWEEKLY', label: 'Biweekly' },
  { value: 'BILLING_PERIOD_MONTHLY', label: 'Monthly' },
  { value: 'BILLING_PERIOD_QUARTERLY', label: 'Quarterly' },
  { value: 'BILLING_PERIOD_SEMIANNUAL', label: 'Semiannual' },
  { value: 'BILLING_PERIOD_ANNUAL', label: 'Annual' },
];

const EMPTY_FORM = {
  name: '',
  email: '',
  phone: '',
  line1: '',
  line2: '',
  city: '',
  state: '',
  postalCode: '',
  country: '',
  taxId: '',
  taxIdType: '',
  taxExempt: false,
  currency: 'CURRENCY_USD',
  billingPeriod: 'BILLING_PERIOD_MONTHLY',
  paymentTermsDays: '30',
  autoCharge: true,
  emailInvoices: true,
  emailReceipts: true,
};

export function BillingAccountFormDialog({ open, onClose, onCreated, onError }: BillingAccountFormDialogProps) {
  const { t } = useI18n();
  const store = useBillingBillingAccount();
  const [form, setForm] = useState(EMPTY_FORM);
  const [moreOpen, setMoreOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const set = <K extends keyof typeof EMPTY_FORM>(field: K, value: (typeof EMPTY_FORM)[K]) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const dirty = !!(form.name.trim() || form.email.trim() || form.phone.trim());

  const reset = () => {
    setForm(EMPTY_FORM);
    setMoreOpen(false);
  };

  const close = () => {
    reset();
    onClose();
  };

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim() || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const account = await store.CreateBillingAccount({
        entity: {} as any,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        billing_address: {
          line1: form.line1.trim(),
          line2: form.line2.trim(),
          city: form.city.trim(),
          state: form.state.trim(),
          postal_code: form.postalCode.trim(),
          country: form.country.trim(),
        },
        tax_info: {
          tax_id: form.taxId.trim(),
          tax_id_type: form.taxIdType.trim(),
          tax_exempt: form.taxExempt,
        },
        payment_settings: {
          currency: form.currency as any,
          billing_period: form.billingPeriod as any,
          payment_terms_days: form.paymentTermsDays.trim() ? Number(form.paymentTermsDays) : 30,
          auto_charge: form.autoCharge,
          email_invoices: form.emailInvoices,
          email_receipts: form.emailReceipts,
        },
        metadata: {},
      } as any);
      reset();
      onCreated(account);
    } catch (err) {
      onError(errMessage(err, t('billing.account.saveError')));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={close}
      onSubmit={submit}
      submitLabel={t('billing.account.createSubmit')}
      canSubmit={!!form.name.trim() && !!form.email.trim()}
      isSubmitting={isSubmitting}
      dirty={dirty}
      width="lg"
      aria-label={t('billing.account.createTitle')}
      footerLeft={
        <button
          type="button"
          onClick={() => setMoreOpen((v) => !v)}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          {moreOpen ? t('billing.account.fewerFields') : t('billing.account.moreFields')}
        </button>
      }
    >
      <div className="space-y-2 px-3.5 pb-2 pt-2">
        <TitleInput
          value={form.name}
          onChange={(v) => set('name', v)}
          placeholder={t('billing.account.namePlaceholder')}
          initialFocus
          onEnter={() => document.getElementById('billing-account-create-email')?.focus()}
          aria-label={t('billing.account.namePlaceholder')}
        />
        <div className="grid grid-cols-2 gap-3">
          <input
            id="billing-account-create-email"
            type="email"
            value={form.email}
            onChange={(e) => set('email', e.target.value)}
            placeholder={t('billing.account.emailPlaceholder')}
            autoCapitalize="off"
            className="w-full rounded-md border border-dashed border-input bg-transparent px-2 py-1 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none"
          />
          <input
            type="text"
            value={form.phone}
            onChange={(e) => set('phone', e.target.value)}
            placeholder={t('billing.account.phonePlaceholder')}
            autoCapitalize="off"
            className="w-full rounded-md border border-dashed border-input bg-transparent px-2 py-1 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none"
          />
        </div>
      </div>

      {moreOpen && (
        <div className="space-y-4 border-t border-border px-3.5 py-3">
          <div>
            <h4 className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t('billing.account.billingAddress')}
            </h4>
            <div className="space-y-1">
              <FormRow label={t('billing.account.addressLine1')} labelWidth="sm">
                <input value={form.line1} onChange={(e) => set('line1', e.target.value)} className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
              </FormRow>
              <FormRow label={t('billing.account.addressLine2')} labelWidth="sm">
                <input value={form.line2} onChange={(e) => set('line2', e.target.value)} className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
              </FormRow>
              <FormRow label={t('billing.account.city')} labelWidth="sm">
                <input value={form.city} onChange={(e) => set('city', e.target.value)} className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
              </FormRow>
              <FormRow label={t('billing.account.state')} labelWidth="sm">
                <input value={form.state} onChange={(e) => set('state', e.target.value)} className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
              </FormRow>
              <FormRow label={t('billing.account.postalCode')} labelWidth="sm">
                <input value={form.postalCode} onChange={(e) => set('postalCode', e.target.value)} className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
              </FormRow>
              <FormRow label={t('billing.account.country')} labelWidth="sm">
                <input value={form.country} onChange={(e) => set('country', e.target.value.toUpperCase())} placeholder="US" className="w-24 rounded-md border border-input bg-background px-2 py-1.5 font-mono text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
              </FormRow>
            </div>
          </div>

          <div>
            <h4 className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t('billing.account.taxInfo')}
            </h4>
            <div className="space-y-1">
              <FormRow label={t('billing.account.taxId')} labelWidth="sm">
                <input value={form.taxId} onChange={(e) => set('taxId', e.target.value)} className="w-full rounded-md border border-input bg-background px-2 py-1.5 font-mono text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
              </FormRow>
              <FormRow label={t('billing.account.taxIdType')} labelWidth="sm">
                <input value={form.taxIdType} onChange={(e) => set('taxIdType', e.target.value)} placeholder="vat, ein, gst" className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
              </FormRow>
              <FormRow label={t('billing.account.taxExempt')} labelWidth="sm">
                <ToggleSwitch label="" checked={form.taxExempt} onChange={(v) => set('taxExempt', v)} className="justify-start" />
              </FormRow>
            </div>
          </div>

          <div>
            <h4 className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t('billing.account.paymentSettings')}
            </h4>
            <div className="space-y-1">
              <FormRow label={t('billing.account.currency')} labelWidth="sm">
                <Select value={form.currency} onChange={(e) => set('currency', e.target.value)} options={CURRENCY_OPTIONS} fullWidth={false} className="w-32" />
              </FormRow>
              <FormRow label={t('billing.account.billingPeriod')} labelWidth="sm">
                <Select value={form.billingPeriod} onChange={(e) => set('billingPeriod', e.target.value)} options={BILLING_PERIOD_OPTIONS} fullWidth={false} className="w-40" />
              </FormRow>
              <FormRow label={t('billing.account.paymentTermsDays')} labelWidth="sm">
                <input
                  type="text"
                  inputMode="numeric"
                  value={form.paymentTermsDays}
                  onChange={(e) => set('paymentTermsDays', e.target.value.replace(/[^0-9]/g, ''))}
                  placeholder="30"
                  className="w-20 rounded-md border border-input bg-background px-2 py-1.5 text-right text-sm tabular-nums text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </FormRow>
              <FormRow label={t('billing.account.autoCharge')} labelWidth="sm">
                <ToggleSwitch label="" checked={form.autoCharge} onChange={(v) => set('autoCharge', v)} className="justify-start" />
              </FormRow>
              <FormRow label={t('billing.account.emailInvoices')} labelWidth="sm">
                <ToggleSwitch label="" checked={form.emailInvoices} onChange={(v) => set('emailInvoices', v)} className="justify-start" />
              </FormRow>
              <FormRow label={t('billing.account.emailReceipts')} labelWidth="sm">
                <ToggleSwitch label="" checked={form.emailReceipts} onChange={(v) => set('emailReceipts', v)} className="justify-start" />
              </FormRow>
            </div>
          </div>
        </div>
      )}
    </FormDialog>
  );
}

export default BillingAccountFormDialog;
